"use server";

import { cookies } from "next/headers";
import { redirect } from "next/navigation";
import { phpFetch } from "@/lib/api";

// Návratová štruktúra pre useActionState v LoginForm.
// success: true = login OK, klient redirect na "/"
// success: false + message = ukáž chybu používateľovi
type LoginState = {
  success: boolean;
  message?: string;
};

/**
 * Server Action pre prihlásenie.
 * Flow: získať CSRF token z PHP -> POST credentials + token na PHP login.php
 * -> ak úspech, forward Set-Cookie ERPSESSID z PHP do browseru.
 *
 * Mirror dev/app/login/actions.ts s erp zmenami:
 *   - ERPSESSID namiesto PHPSESSID
 *   - phpFetch (forwarduje IP hlavičky automaticky) namiesto raw fetch
 *   - Cleanovejšie error handling (žiadne console.log spam)
 */
export async function login(
  _prevState: LoginState,
  formData: FormData,
): Promise<LoginState> {
  const username = String(formData.get("username") ?? "");
  const password = String(formData.get("password") ?? "");

  // 1. CSRF token (jednorazový, viazaný na actionId + IP)
  const csrfRes = await phpFetch(
    "/auth/getCsrfToken.php?actionId=login",
  );
  if (!csrfRes.ok) {
    return {
      success: false,
      message: `Chyba pri získavaní CSRF tokenu (HTTP ${csrfRes.status})`,
    };
  }

  const csrfData = await csrfRes.json();
  if (!csrfData.success || !csrfData.csrfToken) {
    return {
      success: false,
      message: csrfData.message || "Chyba CSRF tokenu",
    };
  }

  // 2. Login attempt
  const loginRes = await phpFetch("/auth/login.php", {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      username,
      password,
      csrfToken: csrfData.csrfToken,
      actionId: "login",
    }),
  });

  // 401/403/500 - PHP nám už dal správu, použijeme ju ak je k dispozícii
  if (!loginRes.ok) {
    let message = "Neplatné prihlasovacie údaje alebo nemáte prístup do erp.";
    try {
      const errData = await loginRes.json();
      if (errData?.message) message = errData.message;
    } catch {
      // PHP vrátilo non-JSON, použijeme generický text
    }
    return { success: false, message };
  }

  const loginData = await loginRes.json();
  if (!loginData.success) {
    return {
      success: false,
      message: loginData.message || "Prihlásenie zlyhalo.",
    };
  }

  // 3. Forward ERPSESSID cookie z PHP response do browseru.
  // PHP odpoveď obsahuje Set-Cookie hlavičku - tú zachytíme a nastavíme
  // ako Next cookie. Bez tohto by browser session cookie nedostal a každé
  // ďalšie volanie by išlo bez session.
  const setCookieHeader = loginRes.headers.get("Set-Cookie");
  if (setCookieHeader) {
    const match = setCookieHeader.match(/ERPSESSID=([^;]+)/);
    if (match) {
      const cookieStore = await cookies();
      cookieStore.set("ERPSESSID", match[1], {
        httpOnly: true,
        secure: process.env.NODE_ENV === "production",
        sameSite: "lax",
        path: "/",
      });
    }
  }

  return { success: true };
}

/**
 * Server Action pre odhlásenie.
 * 1. Zavolá PHP /auth/logout.php aby zničil session na server strane
 * 2. Zmaže ERPSESSID cookie v browseri
 * 3. Server-side redirect na /login (Next handle-ne navigáciu, klient nič
 *    nemusí robiť - cleaner ako client router.push v komponente)
 *
 * Pozn.: redirect() throw-uje RedirectError ktorú Next zachytí, takže funkcia
 * formálne nikdy "neukončí". Návratový typ never reflektuje túto skutočnosť.
 */
export async function logout(): Promise<never> {
  const cookieStore = await cookies();
  const erpSessId = cookieStore.get("ERPSESSID");

  // Voláme PHP iba ak máme cookie - bez nej PHP aj tak nemá čo zničiť
  if (erpSessId) {
    try {
      await phpFetch("/auth/logout.php");
    } catch (e) {
      console.error("ERR #LOGOUT: PHP logout zlyhal:", e);
      // Pokračujeme - chceme cookie zmazať aj keď PHP zlyhalo
    }
  }

  // Zmazanie cookie v browseri (set s expires=past = delete)
  cookieStore.set("ERPSESSID", "", { expires: new Date(0), path: "/" });

  // Server-side redirect - Next handle-ne navigáciu, browser auto-redirect.
  // Bez tohto by klient musel volať router.push, čo niekedy zlyháva po
  // Server Action (chýba serverová indikácia "stav sa zmenil").
  redirect("/login");
}
